import {HexColor, HSLRecord, RGBRecord, RGBTuple} from '../../@types/colors';
import ColorMutator, {Matrix} from './ColorMutator';
import {HexToRGB} from './HexToRGB';
import isRgbRecord from './isRgbRecord';

export default class MutableHex<TDefault extends undefined | null | string | number | symbol = undefined> {
    #rgbTuple?: RGBTuple | null = undefined;
    #mutator?: ColorMutator | null = undefined;

    constructor(protected hexColor: HexColor, private readonly defaultValue: TDefault) {
    }

    logShade(percent: number): MutableHex<TDefault> {
        this.mutator?.logShade(percent);

        return this;
    }

    linearShade(percent: number): MutableHex<TDefault> {
        this.mutator?.linearShade(percent);

        return this;
    }

    lighten(percent: number, linear = true): MutableHex<TDefault> {
        this.mutator?.lighten(percent, linear);

        return this;
    }

    darken(percent: number, linear = true): MutableHex<TDefault> {
        this.mutator?.darken(percent, linear);

        return this;
    }

    hueRotate(angle = 0): MutableHex<TDefault> {
        this.mutator?.hueRotate(angle);

        return this;
    }

    grayscale(value = 1): MutableHex<TDefault> {
        this.mutator?.grayscale(value);

        return this;
    }

    sepia(value = 1): MutableHex<TDefault> {
        this.mutator?.sepia(value);

        return this;
    }

    saturate(value = 1): MutableHex<TDefault> {
        this.mutator?.saturate(value);

        return this;
    }

    multiply(matrix: Matrix): MutableHex<TDefault> {
        this.mutator?.multiply(matrix);

        return this;
    }

    brightness(value = 1): MutableHex<TDefault> {
        this.mutator?.brightness(value);

        return this;
    }

    contrast(value = 1): MutableHex<TDefault> {
        this.mutator?.contrast(value);

        return this;
    }

    linear(slope = 1, intercept = 0): MutableHex<TDefault> {
        this.mutator?.linear(slope, intercept);

        return this;
    }

    invert(value = 1): MutableHex<TDefault> {
        this.mutator?.invert(value);

        return this;
    }

    hsl(): HSLRecord | TDefault {
        return this.mutator?.hsl() ?? this.defaultValue;
    }

    rgb(): RGBRecord | TDefault {
        return this.mutator?.rgb() ?? this.defaultValue;
    }

    hex(): HexColor | TDefault {
        return this.mutator?.hex() ?? this.defaultValue;
    }

    toString(): string {
        let rgb = this.rgb();
        if (!isRgbRecord(rgb)) {
            rgb = {R: 0, G: 0, B: 0};
        }

        const {R, G, B} = rgb;

        return `rgb(${Math.round(R)}, ${Math.round(G)}, ${Math.round(B)})`;
    }

    protected get rgbTuple(): RGBTuple | null {
        if (this.#rgbTuple === undefined) {
            this.#rgbTuple = HexToRGB(this.hexColor);
        }

        return this.#rgbTuple;
    }

    protected get mutator(): ColorMutator | null {
        if (this.#mutator === undefined) {
            this.#mutator = this.rgbTuple ? new ColorMutator(this.rgbTuple) : null;
        }

        return this.#mutator;
    }
}
